import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";

// Internal imports
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { checkUsername as checkUsernameAction, registerSubscriber } from '../../../../shared/actions/authActions';
import { showError } from '../../../../shared/utils/swalHandler';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControlWithFocus,
  FormMessageWithValidation,
  FormDescription,
  useFormValidation,
} from "../../../../shared/components/form";
import { VALIDATION_STATUS } from '../constant';
import {
  validateUsername,
  sanitizeUsernameInput,
  validateEmail,
  removePhoneNumberFormatting
} from '../../../../shared/utils/function';
import PhoneInputWithCountry from '../../../../shared/components/PhoneInputWithCountry';
import HeaderSection from '../../../../components/layout/header';
import { getCreatorRegistrationErrorMessage } from '../getCreatorRegistrationErrorMessage';

/** Escape HTML for safe use in registration error list */
const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Build ul/li HTML from an array of error messages for the Registration Failed modal */
const formatRegistrationErrorList = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return '';
  const items = messages.map((m) => `<li>${escapeHtml(m)}</li>`).join('');
  return `<ul class="registration-error-list">${items}</ul>`;
};

/**
 * SubscriberRegistration component for handling subscriber account registration
 * @returns {JSX.Element} The registration form component
 */
const SubscriberRegister = ({
  form,
  onBack,
}) => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const {
    validateAndFocus,
    shouldShowError,
  } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  // State management
  const [uiState, setUiState] = useState({
    showPassword: false,
    showConfirmPassword: false,
    isLoading: false,
    isUsernameAvailable: null,
    isEmailValid: null
  });

  const [address, setAddress] = useState('');

  // Get selected interests from form
  const selectedInterests = form.watch('selectedInterests') || [];

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate.toSubscriberSignup();
    }
  }, [navigate, onBack]);

  const checkUsername = useCallback(async (username) => {
    if (username.length < 1 || !validateUsername(username)) {
      setUiState(prev => ({ ...prev, isUsernameAvailable: false }));
      return;
    }

    try {
      const result = await dispatch(checkUsernameAction(username));
      const isAvailable = result.success && result.data.isValid;
      setUiState(prev => ({ ...prev, isUsernameAvailable: isAvailable }));
    } catch (error) {
      setUiState(prev => ({ ...prev, isUsernameAvailable: false }));
    }
  }, [dispatch]);

  // When returning to this step (e.g. from Interests), re-run username/email checks
  // so we don't show loading for an already-validated username
  useEffect(() => {
    const username = form.getValues('username');
    if (username && username.length >= 3 && validateUsername(username)) {
      checkUsername(username);
    }
    const email = form.getValues('email');
    if (email) {
      setUiState(prev => ({ ...prev, isEmailValid: validateEmail(email) }));
    }
  }, [form, checkUsername]);

  const handleAddressSelect = useCallback((selectedAddress) => {
    geocodeByAddress(selectedAddress)
      .then((results) => {
        const components = results[0].address_components;
        let city = "";
        let state = "";
        let country = "";

        components.forEach((component) => {
          const { types, long_name, short_name } = component;
          // Try to get city from different possible types
          if (!city && types.includes("locality")) {
            city = long_name;
          }
          // Fallback to sublocality if locality is not available
          if (!city && (types.includes("sublocality_level_1") || types.includes("sublocality"))) {
            city = long_name;
          }
          if (types.includes("administrative_area_level_1")) state = long_name;
          if (types.includes("country")) country = short_name;
        });

        // If city is still not found, extract from the selected address (first part before comma)
        if (!city && selectedAddress) {
          city = selectedAddress.split(',')[0].trim();
        }

        // Special case for New York
        if (state === "New York" && city === "") {
          city = state;
        }

        // Set address state to just the city name so the input shows only the city
        // setAddress(selectedAddress);
        setAddress(city);
        form.setValue('city', city);
        form.setValue('state', state);
        form.setValue('country', country);
        form.trigger(['city', 'state', 'country']);
      })
      .catch(() => {
        // Handle error silently or show user-friendly message
        toast.error('Failed to get location details. Please try again.');
      });
  }, [form]);

  const handleClearLocation = useCallback(() => {
    setAddress('');
    form.setValue('city', '');
    form.setValue('state', '');
    form.setValue('country', '');
    form.trigger(['city', 'state', 'country']);
  }, [form]);

  const onSubmit = useCallback(async (data) => {
    const isValid = await validateAndFocus();
    if (!isValid) return;

    if (!uiState.isUsernameAvailable) {
      toast.error('Please choose a valid username');
      setUiState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    if (!uiState.isEmailValid) {
      toast.error('Please enter a valid email address');
      setUiState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Prepare data for API call
    const registrationData = {
      email: data.email,
      password: data.password,
      username: data.username,
      phone_country_code: data.phoneCountry || "+1",
      phone_number: removePhoneNumberFormatting(data.phoneNumber),
      country: data.country,
      city: data.city,
      state: data.state,
      categories: data.selectedInterests,
    };

    setUiState(prev => ({ ...prev, isLoading: true }));
    try {
      const result = await dispatch(registerSubscriber(registrationData));
      if (result.success) {
        navigate.toResendVerification(data.email);
      } else {
        const messages = getCreatorRegistrationErrorMessage(
          result.error || "Something went wrong. Please try again.",
          result.fields
        );
        const registrationErrorHtml = formatRegistrationErrorList(messages);
        await showError("Registration Failed", registrationErrorHtml, {
          customClass: {
            popup: 'registration-failed-modal',
            htmlContainer: 'registration-error-container'
          }
        });
      }
    } catch (error) {
      const fields = error.error?.details ?? error.fields ?? error.response?.data?.fields;
      const rawMessage = error.response?.data?.mongoCode === 11000
        ? "Account with that email already exists"
        : error.response?.data?.message || error.message || "Something went wrong. Please try again.";
      const messages = getCreatorRegistrationErrorMessage(rawMessage, fields);
      const registrationErrorHtml = formatRegistrationErrorList(messages);
      await showError("Registration Failed", registrationErrorHtml, {
        customClass: {
          popup: 'registration-failed-modal',
          htmlContainer: 'registration-error-container'
        }
      });
    } finally {
      setUiState(prev => ({ ...prev, isLoading: false }));
    }
  }, [validateAndFocus, uiState.isUsernameAvailable, uiState.isEmailValid, navigate, dispatch]);

  const handleLoginLink = useCallback(() => {
    navigate.toLogin();
  }, [navigate]);

  const handleTermsClick = useCallback(() => {
    navigate.toTerms();
  }, [navigate]);

  const handlePrivacyClick = useCallback(() => {
    navigate.toPrivacy();
  }, [navigate]);

  // Validation status functions
  const getUsernameStatus = useMemo(() => {
    const username = form.watch('username');
    if (!username) return VALIDATION_STATUS.EMPTY;
    if (username.length < 1) return VALIDATION_STATUS.INVALID;
    if (uiState.isUsernameAvailable === null) return VALIDATION_STATUS.CHECKING;
    return uiState.isUsernameAvailable ? VALIDATION_STATUS.VALID : VALIDATION_STATUS.INVALID;
  }, [form.watch('username'), uiState.isUsernameAvailable]);

  const getPasswordStatus = useMemo(() => {
    const password = form.watch('password');
    const passwordErrors = form.formState.errors.password;
    if (!password) return VALIDATION_STATUS.EMPTY;
    return passwordErrors ? VALIDATION_STATUS.INVALID : VALIDATION_STATUS.VALID;
  }, [form.watch('password'), form.formState.errors.password]);

  const getConfirmPasswordStatus = useMemo(() => {
    const password = form.watch('password');
    const confirmPassword = form.watch('confirmPassword');
    if (!confirmPassword) return VALIDATION_STATUS.EMPTY;
    return password === confirmPassword ? VALIDATION_STATUS.VALID : VALIDATION_STATUS.INVALID;
  }, [form.watch('password'), form.watch('confirmPassword')]);

  const handleEmailChange = useCallback((field, value) => {
    field.onChange(value);
    setUiState(prev => ({ ...prev, isEmailValid: validateEmail(value) }));
  }, []);

  const handleUsernameChange = useCallback(async (field, value) => {
    const sanitized = sanitizeUsernameInput(value);
    field.onChange(sanitized);

    const isUsernameAvailable = sanitized === "" ? null : sanitized.length >= 1 ? null : false;
    setUiState(prev => ({ ...prev, isUsernameAvailable }));

    // Only call checkUsername API if username passes validation
    if (sanitized.length >= 3) {
      // Trigger validation and wait for it to complete
      const isValid = await form.trigger('username');

      // Only call the API if validation passed
      if (isValid) {
        checkUsername(sanitized);
      }
    }
  }, [checkUsername, form]);

  return (
    <div className={`creator-registration-screen`}>
      <ToastContainer />

      {/* Animated Background Elements */}
      <div className="modern-bg-elements">
        <div className="modern-bg-blur-1"></div>
        <div className="modern-bg-blur-2"></div>
        <div className="modern-bg-blur-3"></div>
      </div>

      {/* Header */}
      <HeaderSection
        type="text"
        title="Register"
        handleBack={handleBack}
      />

      {/* Main Content */}
      <main className="modern-main">
        <div className="modern-welcome-section">
          <div className="modern-welcome-icon">
            <i className="fas fa-user-plus"></i>
          </div>
          <h2 className="modern-welcome-title">Join Our Community</h2>
          <p className="modern-welcome-description">Create your subscriber account to get started</p>
          {selectedInterests.length > 0 && (
            <div className="modern-tags-preview">
              <p className="modern-tags-text">
                <i className="fas fa-tags"></i>
                {selectedInterests.length} interest{selectedInterests.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="modern-form" autoComplete="off">
            {/* Email & Username */}
            <div className="modern-form-section">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="modern-form-field">
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-envelope"></i>
                      Email Address <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="email">
                      <div className="modern-input-container">
                        <input
                          type="email"
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          className={`modern-input 
                            ${uiState.isEmailValid === false ? 'invalid' : uiState.isEmailValid === true ? 'valid' : ''} 
                            ${shouldShowError('email') ? 'error' : ''}`
                          }
                          placeholder="your@email.com"
                          {...field}
                          onChange={(e) => handleEmailChange(field, e.target.value)}
                        />
                        {uiState.isEmailValid === true && (
                          <div className="modern-validation-icon valid">
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                        {uiState.isEmailValid === false && (
                          <div className="modern-validation-icon invalid">
                            <i className="fas fa-times"></i>
                          </div>
                        )}
                      </div>
                    </FormControlWithFocus>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem className="modern-form-field" style={{ marginTop: "25px" }}>
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-at"></i>
                      Username <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="username">
                      <>
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${getUsernameStatus} ${shouldShowError('username') ? 'error' : ''}`}
                            placeholder="@username"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                            maxLength={30}
                            {...field}
                            onChange={(e) => handleUsernameChange(field, e.target.value)}
                            autoFocus={false}
                          />

                          {getUsernameStatus === VALIDATION_STATUS.VALID && (
                            <div className="modern-validation-icon valid">
                              <i className="fas fa-check"></i>
                            </div>
                          )}
                          {getUsernameStatus === VALIDATION_STATUS.INVALID && (
                            <div className="modern-validation-icon invalid">
                              <i className="fas fa-times "></i>
                            </div>
                          )}
                          {getUsernameStatus === VALIDATION_STATUS.CHECKING && (
                            <div className="modern-validation-icon checking">
                              <i className="fas fa-spinner fa-spin"></i>
                            </div>
                          )}
                        </div>
                        <p className="modern-field-hint">
                          <i className="modern-hint-icon fas fa-info-circle"></i>
                          Minimum 3 characters
                        </p>
                      </>
                    </FormControlWithFocus>
                    {field.value && (
                      <div className="modern-field-message">
                        {getUsernameStatus === VALIDATION_STATUS.INVALID && field.value.length > 2 && (
                          <p className="modern-error-text">
                            <i className="fas fa-exclamation-triangle"></i>
                            This username is taken
                          </p>
                        )}
                        {getUsernameStatus === VALIDATION_STATUS.VALID && (
                          <p className="modern-success-text">
                            <i className="fas fa-check-circle"></i>
                            Username is available
                          </p>
                        )}
                      </div>
                    )}
                    {form.watch('username') && (
                      <FormMessageWithValidation
                        fieldName="username"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Password Fields */}
            <div className="modern-form-section">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="modern-form-field">
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-lock"></i>
                      Password <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="password">
                      <div className="modern-input-container">
                        <input
                          type={uiState.showPassword ? "text" : "password"}
                          className={`modern-input ${getPasswordStatus} ${shouldShowError('password') ? 'error' : ''}`}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setUiState(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                          className="modern-password-toggle"
                        >
                          <FontAwesomeIcon icon={uiState.showPassword ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    <FormDescription className="modern-password-hint">
                      <p className="modern-password-requirements">
                        <i className="modern-hint-icon fas fa-shield-halved"></i>
                        Must be at least 6 characters with uppercase, lowercase, number, and special character
                      </p>
                    </FormDescription>
                    {field.value && (
                      <div className="modern-field-message">
                        {getPasswordStatus === VALIDATION_STATUS.VALID && (
                          <p className="modern-success-text">
                            <i className="fas fa-check-circle"></i>
                            Password meets all requirements
                          </p>
                        )}
                      </div>
                    )}
                    {form.watch('password') && (
                      <FormMessageWithValidation
                        fieldName="password"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="modern-form-field mt-3">
                    <FormLabel className="modern-field-label">
                      <i className="modern-field-icon fas fa-lock-open"></i>
                      Confirm Password <span className="modern-field-required">*</span>
                    </FormLabel>
                    <FormControlWithFocus fieldName="confirmPassword">
                      <div className="modern-input-container">
                        <input
                          type={uiState.showConfirmPassword ? "text" : "password"}
                          className={`modern-input ${getConfirmPasswordStatus} ${shouldShowError('confirmPassword') ? 'error' : ''}`}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setUiState(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                          className="modern-password-toggle"
                        >
                          <FontAwesomeIcon icon={uiState.showConfirmPassword ? faEye : faEyeSlash} />
                        </button>
                      </div>
                    </FormControlWithFocus>
                    {form.watch('confirmPassword') && (
                      <FormMessageWithValidation
                        fieldName="confirmPassword"
                        useValidation={true}
                        className="fas fa-exclamation-triangle"
                      />
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Phone Number */}
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem className="modern-form-field">
                  <FormLabel className="modern-field-label">
                    <i className="modern-field-icon fas fa-phone"></i>
                    Phone Number
                  </FormLabel>
                  <FormControlWithFocus fieldName="phoneNumber">
                    <PhoneInputWithCountry
                      value={field.value || ''}
                      onChange={(phoneData) => {
                        // Update phone country, country code, and phone number fields
                        form.setValue('phoneCountry', phoneData.dialCode);
                        form.setValue('phoneCountryCode', phoneData.countryCode);
                        form.setValue('phoneNumber', phoneData.phoneNumber);
                        // Trigger validation for all fields to ensure schema-level validation runs
                        // This is necessary because phone validation depends on phoneCountryCode
                        setTimeout(() => {
                          form.trigger();
                        }, 0);
                      }}
                      placeholder="(555) 123-4567"
                      defaultCountry="US"
                    />
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="phoneNumber"
                    useValidation={true}
                  />

                  <span className='lable_info mt-0 mb-0'>
                    Get real-time alerts about all the latest buzz and stay on top of messages from creators.
                  </span>
                </FormItem>
              )}
            />

            {/* Location Section */}
            <div className="modern-form-section">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="modern-form-field">
                    <div className="modern-location-header">
                      <FormLabel className="modern-field-label">
                        <i className="modern-field-icon fas fa-map-marker-alt"></i>
                        City
                      </FormLabel>
                      {(form.watch('city') || form.watch('state') || form.watch('country')) && (
                        <button
                          type="button"
                          onClick={handleClearLocation}
                          className="modern-clear-location-btn"
                        >
                          <i className="fas fa-times"></i>
                          Clear
                        </button>
                      )}
                    </div>
                    <FormControlWithFocus fieldName="city">
                      <div className="modern-input-container">

                        <PlacesAutocomplete
                          value={address}
                          onChange={(value) => {
                            setAddress(value);
                            form.setValue('city', value);
                            // Trigger validation for the city field
                            form.trigger('city');
                          }}
                          onSelect={handleAddressSelect}
                          searchOptions={{
                            types: ['(cities)'],
                            componentRestrictions: {},
                          }}
                        >
                          {({
                            getInputProps,
                            suggestions,
                            getSuggestionItemProps,
                            loading,
                          }) => (
                            <div className="modern-places-container">
                              <input
                                {...(() => {
                                  const inputProps = getInputProps({
                                    placeholder: "Search city...",
                                    className: `modern-input modern-city-input ${shouldShowError('city') ? 'error' : ''}`,
                                    name: 'city',
                                    autoComplete: 'off',
                                    'aria-autocomplete': 'list',
                                    ref: (input) => {
                                      if (input) {
                                        input.setAttribute('data-field-name', 'city');
                                        input.setAttribute('data-focused-field', 'city');
                                        input.setAttribute('data-slot', 'form-control');
                                        field.ref = input;
                                      }
                                    }
                                  });
                                  delete inputProps['aria-activedescendant'];
                                  delete inputProps['role'];
                                  return inputProps;
                                })()}
                              />
                              <div className="modern-city-icon">
                                <i className="fas fa-magnifying-glass"></i>
                              </div>

                              {(loading || suggestions.length > 0) && (
                                <div className="modern-autocomplete-dropdown">
                                  {loading && (
                                    <div className="modern-autocomplete-loading">
                                      <i className="fas fa-spinner fa-spin"></i>
                                      Loading...
                                    </div>
                                  )}
                                  {suggestions.map((suggestion) => {
                                    const isActive = suggestion.active;

                                    return (
                                      <div
                                        key={suggestion.placeId}
                                        {...getSuggestionItemProps(suggestion, { className: `modern-suggestion ${isActive ? 'modern-suggestion-active' : ''}` })}
                                      >
                                        <i
                                          className={`fas fa-map-marker-alt ${suggestion.active ? 'modern-suggestion-icon active' : 'modern-suggestion-icon'}`}
                                        />
                                        <span>{suggestion.description}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </PlacesAutocomplete>

                      </div>
                    </FormControlWithFocus>
                  </FormItem>
                )}
              />
              <span className='lable_info mt-1'>
                Add your city, state, and country to auto-fill some of your credit card details.
              </span>

              <div className="mt-3">
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem className="modern-form-field">
                      <FormLabel className="modern-field-label">
                        <i className="modern-field-icon fas fa-map-marker-alt"></i>
                        State / Province / Region
                      </FormLabel>
                      <FormControlWithFocus fieldName="state">
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${field.value ? 'valid' : ''} ${shouldShowError('state') ? 'error' : ''}`}
                            placeholder="Auto-filled from city selection"
                            autoComplete="off"
                            readOnly
                            {...field}
                          />
                          {field.value && (
                            <div className="modern-validation-icon valid">
                              <i className="fas fa-check"></i>
                            </div>
                          )}
                        </div>
                      </FormControlWithFocus>
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-3">
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem className="modern-form-field mt-3">
                      <FormLabel className="modern-field-label">
                        Country
                      </FormLabel>
                      <FormControlWithFocus fieldName="country">
                        <div className="modern-input-container">
                          <input
                            type="text"
                            className={`modern-input ${field.value ? 'valid' : ''}`}
                            placeholder="Auto-filled from city selection"
                            autoComplete="off"
                            readOnly
                            {...field}
                          />
                          {field.value && (
                            <div className="modern-validation-icon valid">
                              <i className="fas fa-check"></i>
                            </div>
                          )}
                        </div>
                      </FormControlWithFocus>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem id="terms-section" className="terms-section">
                  <FormControlWithFocus fieldName="termsAccepted">
                    <div className="terms-wrapper">
                      <div className="terms-checkbox-container">
                        <input
                          id="termsAccepted"
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="terms-checkbox"
                        />
                      </div>
                      <div className="terms-label-container">
                        <FormLabel htmlFor="termsAccepted" className="terms-label">
                          I confirm that I am at least 18 years old and agree to the{' '}
                          <span className="terms-link" onClick={handleTermsClick}>
                            Terms of Service
                          </span>{' '}
                          and{' '}
                          <span className="terms-link" onClick={handlePrivacyClick}>
                            Privacy Policy
                          </span>
                        </FormLabel>
                      </div>
                    </div>
                  </FormControlWithFocus>
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <button
              type="submit"
              className="creator-register-submit-btn paddingTOp"
              disabled={uiState.isLoading || form.formState.isSubmitting || !form.formState.isValid}
            >
              <span className="creator-register-submit-text">
                {uiState.isLoading || form.formState.isSubmitting ? 'Creating Account...' : 'Create Account'}
              </span>
              <div className="creator-register-submit-shimmer"></div>
            </button>
          </form>
        </Form>

        {/* Login Link */}
        <div className="login-link-box">
          <p className="login-link-text">
            Already have an account?{' '}
            <span
              className="login-link-span modern-login-link"
              onClick={handleLoginLink}
            >
              Log in
            </span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SubscriberRegister; 